import { toInvoiceStatusBadgeProps, toMoney } from '@sequencehq/utils'
import {
  Text,
  Badge,
  Button,
  GreyGrey30,
  IconArrowRight,
  IndigoIndigo60,
  Link,
  Primary13Regular,
  Primary13SemiBold,
  ShadowXs,
  Spinner,
  Table,
  TableContainer,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
  Center,
  Box,
  Flex
} from '@sequencehq/core-components'
import { dateTimeWithFormat } from '@sequencehq/utils'
import { useCustomerContext } from 'Customer/hooks/useCustomerContext'
import { useNavigate } from 'react-router-dom'
import { formatDateRange, MINUTES } from '@sequencehq/utils/dates'
import { useQuery } from '@sequencehq/api'
import { dashboardv99990101Client } from '@sequencehq/api/dashboard/v99990101'
import { CUSTOMER_BILLING_TABLE_LIMIT } from 'Customer/components/tables/billingTables.constants'

export const CreditNoteTable = () => {
  const navigate = useNavigate()
  const { customer } = useCustomerContext()
  const creditNotesQuery = useQuery(
    dashboardv99990101Client.getCreditNotes,
    { customerId: customer.id, limit: CUSTOMER_BILLING_TABLE_LIMIT },
    { staleTime: 1 * MINUTES }
  )

  const creditNotes = creditNotesQuery.data?.items ?? []

  if (!creditNotes) {
    return (
      <Center height="200px" width="100%" mt={4}>
        <Spinner />
      </Center>
    )
  }
  return (
    <>
      <TableContainer
        borderRadius="lg"
        overflow="hidden"
        border={`1px solid ${GreyGrey30}`}
        mt={4}
        width="100%"
        boxShadow={ShadowXs}
      >
        <Table variant="v2" width="100%">
          <Thead>
            <Tr>
              <Th width={100} textAlign="right">
                Amount
              </Th>
              <Th width={140}>Status</Th>
              <Th>Credit note #</Th>
              <Th width={120}>Invoice</Th>
              <Th width={200}>Billing period</Th>
              <Th width={120}>Sent</Th>
            </Tr>
          </Thead>
          <Tbody>
            {creditNotes.length > 0 ? (
              creditNotes.map(creditNote => {
                return (
                  <Tr
                    key={creditNote.id}
                    onClick={() => navigate(`/credit-notes/${creditNote.id}`)}
                  >
                    <Td textAlign="right">
                      <Text
                        {...Primary13Regular}
                        as={Link.Unstyled}
                        to={`/credit-notes/${creditNote.id}`}
                        color={IndigoIndigo60}
                      >
                        {`${toMoney({
                          currency: creditNote.currency,
                          value: creditNote.grossTotal
                        })}`}
                      </Text>
                    </Td>
                    <Td>
                      <Flex gap={2} alignItems="center">
                        <Badge
                          {...toInvoiceStatusBadgeProps({
                            status: creditNote.status,
                            size: 'sm'
                          })}
                        />
                      </Flex>
                    </Td>
                    <Td>{creditNote.creditNoteNumber || '-'}</Td>
                    <Td>
                      {creditNote.invoiceId ? (
                        // The row navigates to the credit note, so the invoice
                        // link must not bubble.
                        <Text
                          {...Primary13Regular}
                          as={Link.Unstyled}
                          to={`/invoices/${creditNote.invoiceId}`}
                          color={IndigoIndigo60}
                          onClick={e => e.stopPropagation()}
                          data-testid={`customer.creditNotes.${creditNote.id}.invoice`}
                        >
                          {creditNote.invoiceNumber ?? 'View invoice'}
                        </Text>
                      ) : (
                        '-'
                      )}
                    </Td>
                    <Td>
                      {creditNote.billingPeriodStart &&
                      creditNote.billingPeriodEnd
                        ? formatDateRange({
                            from: new Date(creditNote.billingPeriodStart),
                            to: new Date(creditNote.billingPeriodEnd)
                          })
                        : '-'}
                    </Td>
                    <Td>
                      {creditNote.issueDate
                        ? dateTimeWithFormat(creditNote.issueDate, 'd MMM yyyy')
                        : '-'}
                    </Td>
                  </Tr>
                )
              })
            ) : (
              <Tr
                sx={{
                  cursor: 'auto!important',
                  _hover: {
                    bgColor: 'inherit!important'
                  }
                }}
              >
                <Td colSpan={6}>
                  <EmptyContent />
                </Td>
              </Tr>
            )}
          </Tbody>
        </Table>
      </TableContainer>
      <Button
        {...Primary13SemiBold}
        style={{ marginTop: '8px' }}
        variant="ghost"
        trailingIcon={<IconArrowRight color={IndigoIndigo60} />}
        as={Link.Unstyled}
        to={`/credit-notes?customerId=${customer.id}`}
        data-testid="customer.creditNotes.viewAll"
      >
        View all credit notes
      </Button>
    </>
  )
}

const EmptyContent = () => {
  return (
    <Box width="400px" margin="12px auto" textAlign="center">
      <Text {...Primary13SemiBold} color="gray.90">
        No credit notes to display
      </Text>
      <Text {...Primary13Regular} color="gray.80">
        You have not created credit notes for this customer yet.
      </Text>
    </Box>
  )
}
